/*
 * The photo editor (owner, 2026-10-25; livewire/media): a photo rotated, flipped, straightened, cropped, parts of it
 * blurred, and its brightness and contrast changed, once it's uploaded or any time after (Editor, beside Replace). It starts from the
 * photo upright and unedited (upright.jpg) with its saved edits, and Save sends them to Livewire (Media::saveEdit):
 * the rotation in degrees clockwise, the crop as fractions of the edited photo, the rest as media.adjust. The original
 * never changes (ProcessMedia makes the web copies from it, VipsUtil::edit doing the same sums in the same order).
 *
 * Cropper.js 2 (owner, 2026-10-26): its elements are in the dialog's markup, defined when the editor first opens
 * (resources/js/cropper.js). Every edit is a transform of the picture under the crop box, in the server's order, so it
 * shows at once and nothing is built again (DESIGN.md: live as you drag). The canvas is the edited photo's shape; the
 * crop stays on the photo.
 */
let library;
const load = () => library ??= import('./cropper.js').then((elements) => {
    Object.values(elements).forEach((element) => element.$define());
});

// The handles: every one the same small square in the accent (Cropper's own are 5px dots, one corner larger), just
// inside its edge or corner, where the canvas doesn't cut it in half, with room around it for a finger
const handles = `
    :host([action$=-resize]):after { width: 12px !important; height: 12px !important; transform: none; }
    :host([action=n-resize]) { top: 0; height: 24px; } :host([action=n-resize]):after { top: 0; left: calc(50% - 6px); }
    :host([action=s-resize]) { bottom: 0; height: 24px; } :host([action=s-resize]):after { top: auto; bottom: 0; left: calc(50% - 6px); }
    :host([action=e-resize]) { right: 0; width: 24px; } :host([action=e-resize]):after { left: auto; right: 0; top: calc(50% - 6px); }
    :host([action=w-resize]) { left: 0; width: 24px; } :host([action=w-resize]):after { left: 0; top: calc(50% - 6px); }
    :host(:is([action=ne-resize], [action=nw-resize], [action=se-resize], [action=sw-resize])) { width: 24px; height: 24px; }
    :host([action=ne-resize]) { top: 0; right: 0; } :host([action=ne-resize]):after { top: 0; left: auto; right: 0; }
    :host([action=nw-resize]) { top: 0; left: 0; } :host([action=nw-resize]):after { top: 0; left: 0; }
    :host([action=se-resize]) { bottom: 0; right: 0; } :host([action=se-resize]):after { top: auto; bottom: 0; left: auto; right: 0; }
    :host([action=sw-resize]) { bottom: 0; left: 0; } :host([action=sw-resize]):after { top: auto; bottom: 0; left: 0; }
`;

// The arrow keys move the crop box (Cropper listens on the page); its Delete would take the box away
const guard = (event) => {
    ['Delete', 'Backspace'].includes(event.key) && !event.target.closest('input, textarea, [contenteditable]') && event.preventDefault();
};

// `fixed`: a crop shape the place requires ("4:3", owner 2026-10-29; livewire:media :ratio): Crop on and held to it.
// `open`: Crop on from the start (Administrator → Metadata → Photo Editor, owner 2026-11-02)
Alpine.data('photoEditor', (url, rotation = 0, crop = null, adjust = null, tools = [], fixed = null, open = false) => {
    // Kept here, not on the component (nothing on screen follows them): the photo's size, whether a change is ours
    // (Cropper's events let through), the resize watcher, the blurred parts' last id
    let photoWidth, photoHeight, ours = false, observer, serial = 0;

    // A part of the photo ({x, y, w, h}, fractions) after a quarter turn right (90) or left (-90), or mirrored left to
    // right before it (across the photo as it stands, or up and down once it's on its side)
    const turned = ({x, y, w, h}, by) => by > 0 ? {x: 1 - y - h, y: x, w: h, h: w} : {x: y, y: 1 - x - w, w: h, h: w};
    const mirrored = (part, sideways) => sideways ? {...part, y: 1 - part.y - part.h} : {...part, x: 1 - part.x - part.w};

    /**
     * How much a W×H photo tilted by $degrees is enlarged so it still fills its box (no empty corners): the largest
     * W×H-shaped part of it that fits inside, centred (VipsUtil::straighten finds the same part)
     */
    const enlarge = (width, height, degrees) => {
        const [cos, sin] = [Math.cos(Math.abs(degrees) * Math.PI / 180), Math.sin(Math.abs(degrees) * Math.PI / 180)];
        return 1 / Math.min(width / (width * cos + height * sin), height / (width * sin + height * cos));
    };

    return {
        state: 'loading',   // loading, ready, saving, failed
        rotation,
        ratio: fixed ?? 'free',     // the crop's shape: free, or width:height ("4:3")
        // The crop box shown (Crop): off till asked for, unless the photo is cropped already, so what's cut shows (always,
        // for a shape the place requires), or the site opens it with Crop on
        cropping: Boolean(fixed) || ((Boolean(crop) || open) && tools.includes('crop')),
        start: null,    // the edits as it opened (payload()), to tell whether any were made
        flipped: Boolean(adjust?.flip),             // mirrored left to right, before it's turned
        brightness: adjust?.brightness ?? 0,        // -50 to 50 (percent; 0 as it was)
        contrast: adjust?.contrast ?? 0,
        angle: adjust?.angle ?? 0,                  // straightened: -45 to 45 degrees clockwise, after the turn
        // Parts blurred (up to 10): fractions of the photo, as the crop, each with an id for the list
        blurs: (adjust?.blur ?? []).map((part) => ({...part, id: ++serial})),
        frame: null,        // where the edited photo is drawn on the canvas, in pixels (layout)

        async init() {
            try {
                // Just uploaded, its copy may still be being made: tried again (a fresh address each time, past what the
                // browser kept) every 1.5 seconds, five times in all, before giving up
                const fetched = (tries = 0) => new Promise((done, fail) => {
                    const src = tries ? `${url}?try=${tries}` : url;
                    Object.assign(new Image(), {onload: () => done(src), onerror: fail, src});
                }).catch((error) => tries < 4 ? new Promise((wait) => setTimeout(wait, 1500)).then(() => fetched(tries + 1)) : Promise.reject(error));
                const [, src] = await Promise.all([load(), fetched()]);
                const {image, selection} = this.$refs;
                this.$refs.box.querySelectorAll('cropper-handle').forEach((handle) => handle.$addStyles(handles));
                image.src = src;
                ({naturalWidth: photoWidth, naturalHeight: photoHeight} = await image.$ready());

                // Only the edits move the picture (no dragging or pinching it, nor Cropper centring it again)
                image.addEventListener('transform', (event) => ours || event.preventDefault());
                selection.addEventListener('change', (event) => this.keep(event));
                window.addEventListener('keydown', guard, true);
                this.$watch('ratio', () => this.shape());
                // Straightened as the slider moves, the crop box kept where it was
                this.$watch('angle', () => this.layout());

                this.layout();
                this.place(crop ?? {x: 0, y: 0, w: 1, h: 1});
                fixed && this.shape();
                // A new width (a phone turned, the window resized): the same crop on the photo as it's drawn again
                observer = new ResizeObserver(() => {
                    const kept = this.fractions();
                    this.layout();
                    this.place(kept);
                });
                observer.observe(this.$refs.box);
                this.state = 'ready';
                this.start = JSON.stringify(this.payload());
            } catch {
                this.state = 'failed';
            }
        },

        destroy() {
            observer?.disconnect();
            window.removeEventListener('keydown', guard, true);
            // Closed after an upload opened it, unsaved: the page is told of the new photo now (Media::shareUpload)
            this.$wire.unshared && this.$wire.shareUpload();
        },

        /**
         * The canvas in the edited photo's shape, as large as fits in the box, in its middle (owner, 2026-11-02: the box
         * is the space the screen leaves, so turning the photo never moves the buttons), and the picture drawn into it:
         * mirrored, turned by rotation and straightened by angle, then enlarged so no corner is empty (VipsUtil::edit's
         * order); frame says where
         */
        layout() {
            const [w, h] = this.rotation % 180 ? [photoHeight, photoWidth] : [photoWidth, photoHeight];
            const box = this.$refs.box.getBoundingClientRect();
            const fit = Math.min(box.width / w, box.height / h);
            const [canvasWidth, canvasHeight] = [w * fit, h * fit];
            Object.assign(this.$refs.canvas.style, {width: `${canvasWidth}px`, height: `${canvasHeight}px`});
            const scale = fit * enlarge(w, h, this.angle);
            const [cos, sin] = [Math.cos((this.rotation + this.angle) * Math.PI / 180), Math.sin((this.rotation + this.angle) * Math.PI / 180)];
            const flip = this.flipped ? -1 : 1;
            // A matrix about the picture's centre, which starts at the canvas's top left at its own size
            ours = true;
            this.$refs.image.$setTransform(flip * scale * cos, flip * scale * sin, -scale * sin, scale * cos,
                canvasWidth / 2 - photoWidth / 2, canvasHeight / 2 - photoHeight / 2);
            ours = false;
            this.frame = {x: (canvasWidth - w * fit) / 2, y: (canvasHeight - h * fit) / 2, width: w * fit, height: h * fit};
        },

        /** The crop box as fractions of the photo ({x, y, w, h}) */
        fractions() {
            const {x, y, width, height} = this.$refs.selection;
            const frame = this.frame;
            return {x: (x - frame.x) / frame.width, y: (y - frame.y) / frame.height, w: width / frame.width, h: height / frame.height};
        },

        /** The crop box set to $crop (fractions of the photo); hidden again unless Crop is on (Cropper shows it on a change) */
        place(crop) {
            const frame = this.frame;
            ours = true;
            this.$refs.selection.$change(frame.x + crop.x * frame.width, frame.y + crop.y * frame.height, crop.w * frame.width, crop.h * frame.height);
            ours = false;
            this.$refs.selection.hidden = this.$refs.shade.hidden = !this.cropping;
        },

        /**
         * The crop box kept on the photo as it's moved or resized: up against the edge it goes past, or (a resize to a
         * set shape, which can't be cut) left where it was
         */
        keep(event) {
            const {x, y, width, height} = event.detail;
            const frame = this.frame;
            const [right, bottom] = [frame.x + frame.width, frame.y + frame.height];
            if (ours || (x >= frame.x - 0.5 && y >= frame.y - 0.5 && x + width <= right + 0.5 && y + height <= bottom + 0.5)) return;
            event.preventDefault();
            const selection = this.$refs.selection;
            const moved = Math.abs(width - selection.width) < 0.5 && Math.abs(height - selection.height) < 0.5;
            if (!moved && this.aspect()) return;
            const [left, top] = [Math.max(x, frame.x), Math.max(y, frame.y)];
            ours = true;
            moved
                ? selection.$change(Math.min(Math.max(x, frame.x), right - width), Math.min(Math.max(y, frame.y), bottom - height), width, height)
                : selection.$change(left, top, Math.min(x + width, right) - left, Math.min(y + height, bottom) - top);
            ours = false;
        },

        /**
         * The crop box held to the chosen shape: the largest crop of it on the photo, centred, unless it's that shape
         * already (turned with the photo); free keeps it as it is
         */
        shape() {
            const selection = this.$refs.selection;
            selection.aspectRatio = this.aspect() || NaN;
            if (this.aspect() && Math.abs(selection.width / selection.height - this.aspect()) > 0.01) this.place(this.fit());
        },

        /** The largest crop of the chosen shape on the photo, centred (all of it when free), or as near `around`'s middle as it fits */
        fit(around = null) {
            const ratio = this.aspect() / (this.frame.width / this.frame.height);
            const [w, h] = !ratio ? [1, 1] : ratio > 1 ? [1, 1 / ratio] : [ratio, 1];
            const [cx, cy] = around ? [around.x + around.w / 2, around.y + around.h / 2] : [0.5, 0.5];
            const keep = (value, most) => Math.min(Math.max(value, 0), most);
            return {x: keep(cx - w / 2, 1 - w), y: keep(cy - h / 2, 1 - h), w, h};
        },

        /** Mirrored left to right (or back), the crop with it */
        mirror() {
            const crop = this.fractions();
            this.flipped = !this.flipped;
            this.layout();
            this.place(mirrored(crop, this.rotation % 180));
            this.blurs = this.blurs.map((part) => ({...mirrored(part, this.rotation % 180), id: part.id}));
        },

        /** A part blurred, in the middle (moved and resized from there) */
        blur() {
            this.blurs.length < 10 && this.blurs.push({x: 0.35, y: 0.425, w: 0.3, h: 0.15, id: ++serial});
        },

        /**
         * Where a blurred part sits on screen, blurred live by what's behind it (CSS), as much as the server will (3% of
         * the photo's longer side, VipsUtil::blur)
         */
        spot(part) {
            if (!this.frame) return {display: 'none'};   // till the photo has its place
            const {x, y, width, height} = this.frame;
            return {
                left: `${x + part.x * width}px`, top: `${y + part.y * height}px`, width: `${part.w * width}px`, height: `${part.h * height}px`,
                backdropFilter: `blur(${0.03 * Math.max(width, height)}px)`,
            };
        },

        /** A blurred part moved, or resized from its corner, by dx and dy (fractions of the photo), kept on it */
        shift(part, dx, dy, resize = false, from = part) {
            const keep = (value, least, most) => Math.min(Math.max(value, least), most);
            resize
                ? Object.assign(part, {w: keep(from.w + dx, 0.02, 1 - part.x), h: keep(from.h + dy, 0.02, 1 - part.y)})
                : Object.assign(part, {x: keep(from.x + dx, 0, 1 - part.w), y: keep(from.y + dy, 0, 1 - part.h)});
        },

        /**
         * A blurred part by keyboard (focused): the arrow keys move it, with Shift resize it, a hundredth of the photo at
         * a time; the crop box's own arrow keys (Cropper) leave it alone, as the key is taken here
         */
        nudge(event, part) {
            const [dx, dy] = {ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]}[event.key] ?? [];
            if (dx === undefined) return;
            event.preventDefault();
            this.shift(part, dx / 100, dy / 100, event.shiftKey);
        },

        /** A blurred part moved (by its middle) or resized (by its corner) as the pointer drags */
        drag(event, part, resize = false) {
            const [startX, startY, from] = [event.clientX, event.clientY, {...part}];
            const move = (moved) => this.shift(part, (moved.clientX - startX) / this.frame.width, (moved.clientY - startY) / this.frame.height, resize, from);
            const end = () => ['pointermove', 'pointerup', 'pointercancel'].forEach((type) => window.removeEventListener(type, type === 'pointermove' ? move : end));
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', end);
            window.addEventListener('pointercancel', end);
        },

        /**
         * Brightness and contrast as the CSS filter that previews them (on the picture, base.css): the server applies
         * the same sums (VipsUtil::tone). None while both are as they were
         */
        tone() {
            return this.brightness || this.contrast ? `brightness(${1 + this.brightness / 100}) contrast(${1 + this.contrast / 100})` : 'none';
        },

        /**
         * A quarter turn left (-90) or right (90), the crop and the blurred parts turned with it (the same parts of the
         * photo), and a set shape too (4:3 becomes 3:4)
         */
        rotate(by) {
            const crop = this.fractions();
            this.rotation = (this.rotation + by + 360) % 360;
            const [across, down] = this.ratio.split(':');
            if (down && !fixed) {
                this.ratio = `${down}:${across}`;
                // Now, not when the ratio's watcher runs: Cropper holds the box to the shape it has
                this.$refs.selection.aspectRatio = this.aspect();
            }
            this.layout();
            // a required shape stays as it is (owner, 2026-10-29: the turned box was the wrong shape, and forced back
            // off the photo): the largest of it on the turned photo, centred where the crop was
            this.place(fixed ? this.fit(turned(crop, by)) : turned(crop, by));
            this.blurs = this.blurs.map((part) => ({...turned(part, by), id: part.id}));
        },

        /** The ratio as a number: width / height, or 0 for any shape */
        aspect() {
            const [width, height] = this.ratio.split(':').map(Number);
            return height ? width / height : 0;
        },

        /**
         * Back to the photo as uploaded, by every tool offered (Media::tools; the others' edits stay): no edit, all of it.
         * A crop or blurred parts that stay are kept on the same parts of the photo as the flip and turn come undone
         */
        reset() {
            const offered = (tool) => tools.includes(tool);
            const back = (part) => {
                let kept = offered('flip') && this.flipped ? mirrored(part, this.rotation % 180) : part;
                for (let turn = offered('rotate') ? this.rotation : 0; turn > 0; turn -= 90) kept = turned(kept, -90);
                return kept;
            };
            const crop = back(this.fractions());
            const blurs = offered('blur') ? [] : this.blurs.map((part) => ({...back(part), id: part.id}));
            Object.assign(this, Object.fromEntries([
                ['rotate', {rotation: 0}], ['flip', {flipped: false}], ['crop', {ratio: fixed ?? 'free', cropping: Boolean(fixed)}], ['straighten', {angle: 0}],
                ['brightness', {brightness: 0}], ['contrast', {contrast: 0}],
            ].filter(([tool]) => offered(tool)).flatMap(([, values]) => Object.entries(values))), {blurs});
            if (offered('crop')) this.$refs.selection.aspectRatio = this.aspect() || NaN;   // now, as in rotate()
            this.layout();
            this.place(offered('crop') ? this.fit() : crop);
        },

        /** What Save sends (Media::saveEdit): the turn, the crop and the adjustments */
        payload() {
            return [this.rotation, this.fractions(), {
                flip: this.flipped, brightness: this.brightness, contrast: this.contrast, angle: this.angle,
                blur: this.blurs.map(({x, y, w, h}) => ({x, y, w, h})),
            }];
        },

        /** Saved (the dialog closes when it is, and the photo says Saved); one that fails leaves the editor ready to try again */
        async save() {
            const edits = this.payload();
            this.state = 'saving';
            try {
                await this.$wire.saveEdit(...edits);
            } finally {
                this.state = 'ready';
            }
        },

        /**
         * ×, Escape, Back or Cancel (owner, 2026-11-02): closed, the browser's own box asking first when there are edits
         * (nothing saves by itself); a press outside does nothing (x-dialog leave)
         */
        leave() {
            if (this.state === 'saving') return;
            if (this.state === 'ready' && JSON.stringify(this.payload()) !== this.start && !confirm('Close the editor? Your changes will be lost.')) return;
            Alpine.$data(this.$root.closest('dialog')).close();
        },
    };
});
